import { Dialog, IconButton, Divider, Paper, Button, Tab } from '@mui/material'
import { Box, styled } from '@mui/system'
import CloseIcon from '@mui/icons-material/Close'
import { useEffect, useState } from 'react'
import { SalesOrder } from '../../../models/models'
import { toastFailure } from '../../../util/toast'
import CTRLoader from '../../ui/loader/CTRloader'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import DetailsTab from './DetailsTab'
import DeliveryAddressTab from './DeliveryAddressTab'
import OrderlinesTab from './OrderlinesTab'
import { getSalesOrderByIdRequest, SingleSalesOrderRelations } from '../../../api/sales-orders'

const Container = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
})

const Content = styled(Box)({
    width: '100%',
})

const FooterContainer = styled(Box)(() => ({
    display: 'flex',
    width: '95%',
    justifyContent: 'space-between',
    margin: '10px 20px 10px 20px',
}))

const FooterButton = styled(Button)(() => ({
    margin: '10px',
}))

interface Props {
    salesOrderId: number
    closeModal: () => void
    onOk?: () => void
    isOpen: boolean
}

enum State {
    Loading = 'Loading',
    Found = 'Found',
    NotFound = 'NotFound',
    Error = 'Error',
}

export type Tabs = 'Details' | 'DeliveryAddress' | 'Orderlines'

export default function InspectSalesOrderModal(props: Props) {
    const { salesOrderId, closeModal, isOpen, onOk } = props
    const [activeTab, setActiveTab] = useState<Tabs>('Details')
    const handleTabChange = (event: React.SyntheticEvent, tabKey: Tabs) => {
        event.preventDefault()
        setActiveTab(tabKey)
    }
    const [salesOrder, setSalesOrder] = useState<SalesOrder>()
    const [state, setState] = useState<State>(State.Loading)

    const getSalesOrder = async () => {
        const response = await getSalesOrderByIdRequest(salesOrderId, {
            relations: [
                SingleSalesOrderRelations.Orderlines,
                SingleSalesOrderRelations.Projects,
                SingleSalesOrderRelations.CallOffs,
                SingleSalesOrderRelations.Customer,
            ],
            withReservedOrderlineAmounts: true,
        })

        if (response.successful) {
            setSalesOrder(response.data)
            setState(State.Found)
        } else if (response.status === 404) {
            setState(State.NotFound)
            toastFailure('Could not find sales order')
        } else {
            setState(State.Error)
            toastFailure('Something went wrong retrieving the sales order')
        }
    }
    useEffect(() => {
        getSalesOrder()
    }, [])

    return (
        <Dialog open={isOpen} onClose={closeModal} fullWidth maxWidth="md">
            <Paper style={{ height: '100%', maxHeight: '800px' }}>
                <Box
                    sx={{
                        fontSize: 16,
                        fontWeight: 'bold',
                        padding: '15px 15px 10px 23px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div> Sales order </div>
                    <IconButton aria-label="close" onClick={closeModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <Container>
                    {state === State.Loading && (
                        <Content
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                                margin: '20px',
                                width: '100%',
                                height: '400px',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <CTRLoader size="xl" />
                        </Content>
                    )}
                    {salesOrder && state === State.Found && (
                        <Content>
                            <TabContext value={activeTab}>
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        padding: '20px 50px 0px 50px',
                                    }}
                                >
                                    <TabList onChange={handleTabChange}>
                                        <Tab
                                            label="Details"
                                            value="Details"
                                            style={{ textTransform: 'none' }}
                                        />
                                        <Tab
                                            label="Delivery Address"
                                            value="DeliveryAddress"
                                            style={{ textTransform: 'none' }}
                                        />
                                        <Tab
                                            label="Order lines"
                                            value="Orderlines"
                                            style={{ textTransform: 'none' }}
                                        />
                                    </TabList>
                                </Box>

                                <TabPanel value="Details">
                                    <DetailsTab salesOrder={salesOrder} />
                                </TabPanel>
                                <TabPanel value="DeliveryAddress">
                                    <DeliveryAddressTab salesOrder={salesOrder} />
                                </TabPanel>
                                <TabPanel value="Orderlines">
                                    <OrderlinesTab salesOrder={salesOrder} />
                                </TabPanel>
                            </TabContext>
                        </Content>
                    )}
                </Container>
            </Paper>
        </Dialog>
    )
}
